
import { useState } from 'react';

interface UsePaginationProps {
  totalItems: number;
  initialPage?: number;
  itemsPerPage?: number;
}

interface UsePaginationReturn {
  currentPage: number;
  setCurrentPage: (page: number) => void;
  itemsPerPage: number;
  setItemsPerPage: (itemsPerPage: number) => void;
  totalPages: number;
  paginatedItems: <T>(items: T[]) => T[];
  startIndex: number;
  endIndex: number;
}

export const usePagination = ({
  totalItems,
  initialPage = 1,
  itemsPerPage: initialItemsPerPage = 10
}: UsePaginationProps): UsePaginationReturn => {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);
  
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  
  // Ensure currentPage is valid whenever total pages changes
  if (currentPage > totalPages) {
    setCurrentPage(totalPages);
  }

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  const paginatedItems = <T>(items: T[]): T[] => {
    return items.slice(startIndex, endIndex);
  };

  return {
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    paginatedItems,
    startIndex,
    endIndex
  };
};
